import { buildCanonicalAdminFinancerRows } from "../constants/adminFinancerFields";
import {
  formatExcelCellValue,
  getBankerFieldsFromCase,
  isBankContactExcelHeader,
  normalizeExcelHeader,
  pickExcelField,
  pickExcelFieldExact,
} from "./bankerExcelFields";
import { isLoanAccountValue } from "./bankerValueUtils";

export const EXCEL_COLUMN_ORDER_KEY = "_excelColumnOrder";

export { ADMIN_FINANCER_DISPLAY_FIELDS } from "../constants/adminFinancerFields";

export function isExcelMetaKey(key) {
  return String(key || "").startsWith("_");
}

export function isNormMirrorKey(key, excelFields) {
  const nk = normalizeExcelHeader(key);
  const raw = String(key).trim();
  if (!raw || raw.toLowerCase() !== nk) return false;
  return Object.keys(excelFields || {}).some((k) => {
    if (k === key) return false;
    return normalizeExcelHeader(k) === nk && String(k).trim().toLowerCase() !== nk;
  });
}

function collectExcelFieldKeys(excelFields = {}) {
  const keys = [];
  const seen = new Set();
  for (const key of Object.keys(excelFields)) {
    if (isExcelMetaKey(key) || isNormMirrorKey(key, excelFields)) continue;
    const nk = normalizeExcelHeader(key);
    if (nk === "casecode" || nk === "case code") continue;
    if (seen.has(nk)) continue;
    seen.add(nk);
    keys.push(String(key).trim());
  }
  return keys;
}

/** Column headers from upload snapshot (used for storage, not admin card order). */
export function getExcelColumnOrder(excelFields = {}, options = {}) {
  const external = options.columnOrder || options.excelColumnOrder;
  if (Array.isArray(external) && external.length) {
    return external.map(String).filter(Boolean);
  }

  if (!excelFields || typeof excelFields !== "object") return [];
  const stored = excelFields[EXCEL_COLUMN_ORDER_KEY];
  if (Array.isArray(stored) && stored.length) {
    return stored.map(String).filter(Boolean);
  }

  return collectExcelFieldKeys(excelFields);
}

/** Walk columns in Excel file order (duplicate headers e.g. two "mobile no"). */
export function listExcelColumnsInFileOrder(excelFields = {}) {
  if (!excelFields || typeof excelFields !== "object") return [];
  const order = excelFields._excelColumnOrder;
  const keys = excelFields._excelColumnKeys;

  if (Array.isArray(order) && Array.isArray(keys) && keys.length === order.length) {
    const cols = [];
    for (let i = 0; i < order.length; i++) {
      const label = String(order[i] || "").trim();
      const key = String(keys[i] || "").trim();
      if (!label || key.startsWith("_")) continue;
      const nk = normalizeExcelHeader(label);
      if (nk === "casecode" || nk === "case code") continue;
      cols.push({
        key,
        label,
        nk,
        value: String(excelFields[key] ?? "").trim(),
      });
    }
    return cols;
  }

  if (Array.isArray(order) && order.length) {
    const occ = {};
    const cols = [];
    for (const label of order) {
      if (!label || String(label).startsWith("_")) continue;
      const nk = normalizeExcelHeader(label);
      if (nk === "casecode" || nk === "case code") continue;
      occ[nk] = (occ[nk] || 0) + 1;
      const storageKey = occ[nk] === 1 ? label : `${label}_${occ[nk]}`;
      cols.push({
        key: storageKey,
        label,
        nk,
        value: String(
          excelFields[storageKey] ?? (occ[nk] === 1 ? excelFields[label] : "") ?? ""
        ).trim(),
      });
    }
    return cols;
  }

  const cols = [];
  for (const [key, val] of Object.entries(excelFields)) {
    if (isExcelMetaKey(key)) continue;
    const nk = normalizeExcelHeader(key);
    if (nk === "casecode" || nk === "case code") continue;
    cols.push({
      key,
      label: key,
      nk,
      value: String(val ?? "").trim(),
    });
  }
  return cols;
}

export function pickExcelCell(excelFields = {}, label = "") {
  if (!excelFields || typeof excelFields !== "object" || !label) return "";
  const direct = excelFields[label];
  if (direct != null) {
    const s = String(direct).trim();
    if (s) return s;
  }
  const want = String(label).trim().toLowerCase();
  for (const [key, val] of Object.entries(excelFields)) {
    if (isExcelMetaKey(key) || isNormMirrorKey(key, excelFields)) continue;
    if (String(key).trim().toLowerCase() === want) {
      const s = String(val ?? "").trim();
      if (s) return s;
    }
  }
  return "";
}

function isMobileHeader(nk) {
  return /^mobile(\s+no)?(\s+\d+)?$/.test(nk) || /^mob(\s+no)?/.test(nk);
}

function isLoanHeader(nk) {
  return (
    nk.includes("loan number") ||
    nk.includes("loan no") ||
    nk.includes("loan account") ||
    nk === "loan" ||
    nk === "lan" ||
    nk.includes("lan no") ||
    (nk.includes("agreement") && (nk.includes("no") || nk.includes("number")))
  );
}

function resolveLoanNumber(doc, e, bankers) {
  const candidates = [
    bankers.loanNumber,
    doc.loanAccountNumber,
    pickExcelField(e, [
      "loan number",
      "loan no",
      "lan",
      "lan no",
      "agreement number",
      "agreement no",
    ]),
  ];
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s && isLoanAccountValue(s)) return s;
  }
  return "";
}

const SKIP_EXTRA_NORMS = new Set([
  "tbr",
  "allocation",
  "casecode",
  "case code",
  "traceexact",
  "snapshotkeys",
  "excelcolumnorder",
  "excelcolumnkeys",
]);

function dashIfEmpty(value) {
  const v = formatExcelCellValue(value);
  return v === "" ? "—" : v;
}

function canonicalCoveredNorms(rows = []) {
  const set = new Set();
  for (const row of rows) {
    const nk = normalizeExcelHeader(row.label);
    if (nk) set.add(nk);
  }
  return set;
}

function buildLoadedDetailRows(doc = {}, excel = {}) {
  const loadedHit = pickExcelFieldExact(excel, [
    "Loaded Details",
    "Loaded Detail",
    "loaded details",
  ]);
  const whatHit = pickExcelFieldExact(excel, [
    "What is loaded",
    "loaded short",
    "What is Loaded",
  ]);
  const loaded = loadedHit.found
    ? loadedHit.value
    : formatExcelCellValue(doc.loadedDetail);
  const what = whatHit.found
    ? whatHit.value
    : formatExcelCellValue(doc.loadedShort);
  return [
    { label: "Loaded Details", value: dashIfEmpty(loaded) },
    { label: "What is loaded", value: dashIfEmpty(what) },
  ];
}

function extraExcelSource(doc = {}) {
  const excel =
    doc.excelFields && typeof doc.excelFields === "object" ? doc.excelFields : {};
  const snapshotKeys = Array.isArray(excel._snapshotKeys) ? excel._snapshotKeys : null;
  const keys =
    snapshotKeys && snapshotKeys.length
      ? snapshotKeys
      : Object.keys(excel).filter((k) => !String(k).startsWith("_"));
  return { excel, keys };
}

function buildExtraExcelRows(doc = {}, canonicalRows = []) {
  const covered = canonicalCoveredNorms(canonicalRows);
  covered.add("loaded details");
  covered.add("loaded detail");
  covered.add("what is loaded");
  covered.add("loaded short");
  const { excel, keys } = extraExcelSource(doc);
  const rows = [];
  const seen = new Set();
  for (const key of keys) {
    if (isExcelMetaKey(key) || isNormMirrorKey(key, excel)) continue;
    const nk = normalizeExcelHeader(key);
    if (!nk || SKIP_EXTRA_NORMS.has(nk) || covered.has(nk) || seen.has(nk)) continue;
    if (isBankContactExcelHeader(key)) continue;
    seen.add(nk);
    rows.push({
      label: String(key).trim(),
      value: dashIfEmpty(excel[key]),
    });
  }
  return rows;
}

/** Fixed SK financer field list for admin UI + bank notify messages. */
export function buildAdminExcelDisplayRows(caseDoc = {}, options = {}) {
  const doc = caseDoc && typeof caseDoc === "object" ? caseDoc : {};
  const e = {
    ...(doc.extraFields && typeof doc.extraFields === "object" ? doc.extraFields : {}),
    ...(doc.excelFields && typeof doc.excelFields === "object" ? doc.excelFields : {}),
  };
  const bankers = getBankerFieldsFromCase(doc);
  const loanResolved = resolveLoanNumber(doc, e, bankers);
  return buildCanonicalAdminFinancerRows(
    { ...doc, excelFields: e },
    bankers,
    loanResolved,
    options
  );
}

/** Admin detail cards: fixed field order, empty shown as — */
export function buildAdminExcelOrderedRows(caseData = {}, { showEmpty = true } = {}) {
  const canonical = buildAdminExcelDisplayRows(caseData)
    .map(({ label, value }) => {
      const v = formatExcelCellValue(value);
      if (!showEmpty && !v) return null;
      return { label, value: dashIfEmpty(v) };
    })
    .filter(Boolean);
  const loaded = buildLoadedDetailRows(
    caseData && typeof caseData === "object" ? caseData : {},
    caseData?.excelFields && typeof caseData.excelFields === "object"
      ? caseData.excelFields
      : {}
  );
  const extras = buildExtraExcelRows(caseData, [...canonical, ...loaded]);
  return [...canonical, ...loaded, ...extras];
}

export const CONFIRMATION_DETAIL_SECTIONS = [
  {
    title: "Vehicle & Customer Information",
    labels: [
      "Registration Numbers",
      "Customer Name",
      "Loan Number",
      "Make",
      "Model",
      "Engine Number",
      "Chasis Number",
      "Chassis Number",
    ],
  },
  {
    title: "Financial & Vehicle Status",
    labels: ["EMI", "POS", "instalment", "Status", "Seasoning"],
  },
  {
    title: "Address & Bank Information",
    labels: ["Address", "Bank / NBFC Name", "Branch Name"],
  },
  {
    title: "Loaded Details",
    labels: ["Loaded Details", "What is loaded"],
  },
  {
    title: "Bank Contact Information",
    labels: [
      "1st bankar Name",
      "mobile no 1",
      "2nd bankar Name",
      "mobile no 2",
      "3rd bankar Name",
      "mobile no 3",
    ],
  },
];

export function groupConfirmationDetailRows(rows = []) {
  const unused = rows.slice();
  const take = (labels) => {
    const set = new Set(labels.map((l) => normalizeExcelHeader(l)));
    const picked = [];
    for (let i = unused.length - 1; i >= 0; i -= 1) {
      if (set.has(normalizeExcelHeader(unused[i].label))) {
        picked.unshift(unused.splice(i, 1)[0]);
      }
    }
    picked.sort(
      (a, b) =>
        labels.findIndex((l) => normalizeExcelHeader(l) === normalizeExcelHeader(a.label)) -
        labels.findIndex((l) => normalizeExcelHeader(l) === normalizeExcelHeader(b.label))
    );
    return picked;
  };

  const sections = [];
  for (const def of CONFIRMATION_DETAIL_SECTIONS) {
    const items = take(def.labels);
    if (def.extras && unused.length) {
      items.push(...unused.splice(0, unused.length));
    }
    if (items.length) sections.push({ title: def.title, rows: items });
  }
  if (unused.length) {
    sections.push({ title: "Additional Excel fields", rows: unused });
  }
  return sections;
}

export { isMobileHeader, isLoanHeader };
